import axios, { AxiosResponse } from 'axios';
import { 
  LoginRequest, 
  LoginResponse, 
  RegisterRequest, 
  RefreshTokenRequest,
  UserProfile 
} from '../types/auth';
import { API_CONFIG, getApiUrl } from '../config/api';

// Создаем экземпляр axios с базовой конфигурацией
const apiClient = axios.create({
  baseURL: API_CONFIG.BASE_URL,
  headers: API_CONFIG.HEADERS,
  timeout: 10000
});

// Функция для обновления токена
const refreshAuthToken = async (refreshToken: string): Promise<LoginResponse> => {
  try {
    const response: AxiosResponse<LoginResponse> = await axios.post(
      API_CONFIG.REFRESH_URL,
      { refreshToken },
      { 
        headers: {
          ...API_CONFIG.HEADERS,
          'Authorization': `Bearer ${refreshToken}`
        },
        timeout: 10000
      }
    );
    return response.data;
  } catch (error) {
    console.error('Refresh token error:', error);
    throw error;
  }
};

// Интерцептор для добавления токена к запросам
apiClient.interceptors.request.use(
  (config: any) => {
    const token = localStorage.getItem('accessToken');
    console.log('Axios interceptor - token:', token);
    console.log('Axios interceptor - URL:', config.url);
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error: any) => {
    return Promise.reject(error);
  }
);

// Интерцептор для обработки ошибок
apiClient.interceptors.response.use(
  (response: any) => response,
  async (error: any) => {
    const originalRequest = error.config;
    
    // Проверяем, что это ошибка 401 и что мы еще не пытались обновить токен
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      const refreshToken = localStorage.getItem('refreshToken');
      if (refreshToken) {
        try {
          console.log('Attempting to refresh token...');
          const response = await refreshAuthToken(refreshToken);
          
          // Сохраняем новые токены
          localStorage.setItem('accessToken', response.accessToken);
          localStorage.setItem('refreshToken', response.refreshToken);
          
          console.log('Token refreshed successfully');
          
          // Повторяем оригинальный запрос с новым токеном
          originalRequest.headers.Authorization = `Bearer ${response.accessToken}`;
          return apiClient(originalRequest);
        } catch (refreshError) {
          console.error('Failed to refresh token:', refreshError);
          // Если не удалось обновить токен, очищаем localStorage и перенаправляем на логин
          localStorage.removeItem('accessToken');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('userId');
          
          // Перенаправляем на страницу логина только если мы не уже на ней
          if (window.location.pathname !== '/login') {
            window.location.href = '/login';
          }
        }
      } else {
        // Нет refresh токена, перенаправляем на логин
        if (window.location.pathname !== '/login') {
          window.location.href = '/login';
        }
      }
    }
    return Promise.reject(error);
  }
);

const authService = {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      console.log('Login - Starting login process');
      const response: AxiosResponse<LoginResponse> = await apiClient.post(
        API_CONFIG.ENDPOINTS.LOGIN,
        credentials
      );
      
      console.log('Login response received:', response.data);
      
      // Сохраняем токены и ID пользователя в localStorage
      if (response.data.accessToken) {
        localStorage.setItem('accessToken', response.data.accessToken);
        localStorage.setItem('refreshToken', response.data.refreshToken);
        
        console.log('Tokens saved to localStorage');
        console.log('Access token length:', response.data.accessToken.length);
        console.log('Refresh token length:', response.data.refreshToken.length);
        
        // Сохраняем ID пользователя если он есть в ответе
        if (response.data.id) {
          localStorage.setItem('userId', response.data.id.toString());
          console.log('User ID saved:', response.data.id);
        }
        
        console.log('Login successful - all data saved');
      } else {
        console.error('No access token in response');
        throw new Error('No access token received from server');
      }
      
      return response.data;
    } catch (error) {
      console.error('Login error:', error);
      throw error;
    }
  },

  async register(userData: RegisterRequest): Promise<LoginResponse> {
    try {
      const response: AxiosResponse<LoginResponse> = await apiClient.post(
        API_CONFIG.ENDPOINTS.REGISTER,
        userData
      );
      
      // Сохраняем токены в localStorage
      if (response.data.accessToken) {
        localStorage.setItem('accessToken', response.data.accessToken);
        localStorage.setItem('refreshToken', response.data.refreshToken);
      }
      
      return response.data;
    } catch (error) {
      console.error('Register error:', error);
      throw error;
    }
  },

  async refreshAuthToken(refreshToken: string): Promise<LoginResponse> {
    try {
      const response: AxiosResponse<LoginResponse> = await axios.post(
        API_CONFIG.REFRESH_URL,
        { refreshToken },
        { 
          headers: {
            ...API_CONFIG.HEADERS,
            'Authorization': `Bearer ${refreshToken}`
          },
          timeout: 10000
        }
      );
      return response.data;
    } catch (error) {
      console.error('Refresh token error:', error);
      throw error;
    }
  },

  async logout(): Promise<void> {
    try {
      await apiClient.post(API_CONFIG.ENDPOINTS.LOGOUT);
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Очищаем токены и ID пользователя из localStorage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('userId');
    }
  },

  async getProfile(): Promise<UserProfile> {
    try {
      const response: AxiosResponse<UserProfile> = await apiClient.get(
        API_CONFIG.ENDPOINTS.PROFILE
      );
      return response.data;
    } catch (error) {
      console.error('Get profile error:', error);
      throw error;
    }
  },

  async getMe(): Promise<any> {
    try {
      const token = this.getAccessToken();
      console.log('getMe - Access token:', token);
      console.log('getMe - Token length:', token ? token.length : 0);
      console.log('getMe - Is authenticated:', this.isAuthenticated());
      
      if (!token) {
        throw new Error('No access token available');
      }

      // Используем axios с правильными заголовками
      const response = await apiClient.get(API_CONFIG.ENDPOINTS.ME);
      
      console.log('getMe - Success response:', response.data);
      
      // Преобразуем ответ API в нужный формат
      const userData = {
        id: response.data.id,
        email: response.data.email,
        username: response.data.username,
        name: response.data.firstName, // API возвращает firstName
        surname: response.data.lastName, // API возвращает lastName
        role: response.data.role,
        isActive: response.data.isActive,
        createdAt: response.data.createdAt,
        updatedAt: response.data.updatedAt
      };
      
      console.log('getMe - Transformed user data:', userData);
      return userData;
    } catch (error) {
      console.error('Get me error:', error);
      throw error;
    }
  },

  // Вспомогательные методы
  isAuthenticated(): boolean {
    const token = localStorage.getItem('accessToken');
    console.log('isAuthenticated check - token:', token);
    return !!token && !this.isTokenExpired();
  },

  isTokenExpired(): boolean {
    const token = localStorage.getItem('accessToken');
    if (!token) return true;
    
    try {
      // Декодируем JWT токен (только payload)
      const payload = JSON.parse(atob(token.split('.')[1]));
      const currentTime = Math.floor(Date.now() / 1000);
      console.log('Token expiration check:', {
        exp: payload.exp,
        currentTime,
        isExpired: payload.exp < currentTime
      });
      return payload.exp < currentTime;
    } catch (error) {
      console.error('Error parsing token:', error);
      return true;
    }
  },

  getUserIdFromToken(): number | null {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      console.log('User ID from token:', payload.sub);
      // Предполагаем, что ID пользователя находится в поле sub или id
      return payload.id || payload.userId || null;
    } catch (error) {
      console.error('Error getting user ID from token:', error);
      return null;
    }
  },

  getUserId(): number | null {
    const userId = localStorage.getItem('userId');
    if (userId) {
      console.log('User ID from localStorage:', userId);
      return Number(userId);
    }
    return null;
  },

  getEmailFromToken(): string | null {
    const token = localStorage.getItem('accessToken');
    if (!token) return null;
    
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      console.log('Email from token:', payload.sub);
      return payload.sub;
    } catch (error) {
      console.error('Error getting email from token:', error);
      return null;
    }
  },

  async getUserByEmail(email: string): Promise<any> {
    try {
      console.log('Getting user by email:', email);
      // Используем DATA API для поиска пользователя по email
      const url = `${API_CONFIG.DATA_URL}${API_CONFIG.ENDPOINTS.USERS}?email=${email}`;
      console.log('User by email URL:', url);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          ...API_CONFIG.HEADERS,
          'Authorization': `Bearer ${this.getAccessToken()}`
        }
      });
      
      console.log('User by email response status:', response.status);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      console.log('User by email response data:', data);
      
      if (data.content && data.content.length > 0) {
        return data.content[0];
      }
      return null;
    } catch (error) {
      console.error('Error getting user by email:', error);
      throw error;
    }
  },

  getAccessToken(): string | null {
    const token = localStorage.getItem('accessToken');
    console.log('getAccessToken - token from localStorage:', token);
    console.log('getAccessToken - token exists:', !!token);
    console.log('getAccessToken - token length:', token ? token.length : 0);
    console.log('getAccessToken - token expired:', this.isTokenExpired());
    return token;
  },

  getRefreshToken(): string | null {
    const token = localStorage.getItem('refreshToken');
    console.log('getRefreshToken - token:', token);
    return token;
  },

  clearTokens(): void {
    console.log('Clearing tokens and user data');
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('userId');
  }
};

export default authService; 